// StanceCompare.js
import React from 'react';
import { Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useApi2Resource from '../../../hooks/useApi2Resource';
import {
  fetchStanceComparison,
  partyColor,
  partyName,
  partyPath,
} from '../../../utils/parties';
import './Parties.css';

/**
 * StanceCompare
 *
 * Every party's platform on one topic, side by side, opened from a stance on
 * a party page. The question a party-first reader asks next is rarely "what
 * else does my party say" and usually "and what do the others say about
 * this" -- this answers it without leaving the page.
 *
 * The party being read is pinned first. The rest keep the API's order.
 *
 * Passages are shown exactly as the API returns them: the party's own words,
 * the first matching passage, the sentences in it that use the topic's words.
 * No summary, no agree/disagree, no placement on a spectrum -- the API makes
 * none of those judgements and neither does this.
 */

const MAX_SENTENCES = 2;

function StanceCompare({ topic, activeKey, stateAbbr }) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { data, loading, error } = useApi2Resource(() => fetchStanceComparison(topic), [topic]);

  if (loading) {
    return (
      <div className="stance-compare stance-compare--loading">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="stance-compare">
        <p className="party-muted">
          {t('common:stanceCompareError', 'The comparison could not be loaded. Try again shortly.')}
        </p>
      </div>
    );
  }

  const rows = Array.isArray(data.parties) ? [...data.parties] : [];
  rows.sort((a, b) => (a.party === activeKey ? -1 : b.party === activeKey ? 1 : 0));

  return (
    <div className="stance-compare">
      {rows.map((row) => {
        const passage = Array.isArray(row.passages) ? row.passages[0] : null;
        const sentences = (passage?.sentences || []).slice(0, MAX_SENTENCES);
        const isActive = row.party === activeKey;

        return (
          <div
            key={row.party}
            className={`stance-compare-row ${isActive ? 'stance-compare-row--active' : ''}`}
            style={{ '--party-color': partyColor(row.party) }}
          >
            {isActive ? (
              <span className="stance-compare-party">{partyName(row.party)}</span>
            ) : (
              <button
                type="button"
                className="stance-compare-party stance-compare-party--link"
                onClick={() => navigate(partyPath(row.party, stateAbbr))}
              >
                {partyName(row.party)}
              </button>
            )}

            {row.addressed && passage ? (
              <>
                {passage.heading && <div className="stance-heading">{passage.heading}</div>}
                {sentences.map((sentence, index) => (
                  <p key={index} className="stance-quote">
                    {sentence}
                  </p>
                ))}
              </>
            ) : (
              <p className="party-muted">
                {row.why ||
                  t('common:stanceNotAddressed', 'Its platform does not use the words for this topic.')}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default StanceCompare;